'use client';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { processHeading, type HeadingType } from '@workspace/lib/heading-utils';
import { INVISIBLES } from '@workspace/constants';
import { AccordionItem, AccordionLink } from '@workspace/interfaces';
import type { SanityBynderDocument, SanityDocumentSection } from './document-block.types';

// Type definitions
interface SanityImage {
  asset: {
    _ref: string;
    _type: 'reference';
  };
  alt?: string;
  hotspot?: any;
  crop?: any;
}

interface DocumentBlockData {
  title?: HeadingType;
  image?: SanityImage;
  customBackgroundColor?: string | { hex: string };
  documentSections: SanityDocumentSection[];
  defaultExpanded?: string[];
  imagePosition?: 'left' | 'right' | 'hidden';
  isEnabled?: boolean;
}

interface FileUrlInfo {
  url: string;
  filename: string;
  extension: string;
  originalFilename?: string;
}

interface UseDocumentBlockReturn {
  // Component state
  fileUrls: Record<string, FileUrlInfo>;

  // Processed data
  resolvedTitleText: string;
  cleanImagePosition: string;
  visibleSections: SanityDocumentSection[];
  transformedAccordionItems: AccordionItem[];
  defaultExpandedIds: string[];

  // Computed properties
  shouldRender: boolean;
  hasVisibleSections: boolean;
}

/**
 * Custom hook for managing DocumentBlock component logic
 * Handles file URL resolution, background color processing, and data transformation
 */
export function useDocumentBlock(block: DocumentBlockData): UseDocumentBlockReturn {
  const { title, documentSections, defaultExpanded = [], imagePosition = 'left', isEnabled = true } = block;

  // State
  const [fileUrls, setFileUrls] = useState<Record<string, FileUrlInfo>>({});

  // Memoized processed title
  const { text: resolvedTitleText } = useMemo(() => {
    return processHeading(title);
  }, [title]);

  // Memoized cleaned image position
  const cleanImagePosition = useMemo(() => {
    return typeof imagePosition === 'string' ? imagePosition.replace(INVISIBLES, '').trim().toLowerCase() : 'left';
  }, [imagePosition]);

  // Memoized visible sections
  const visibleSections = useMemo(() => {
    return documentSections?.filter((section: SanityDocumentSection) => section.isVisible !== false) || [];
  }, [documentSections]);

  // Computed properties
  const shouldRender = isEnabled;

  const hasVisibleSections = visibleSections.length > 0;

  const getBynderDocumentUrl = useCallback((bynderDocument?: SanityBynderDocument): string => {
    if (!bynderDocument) return '';

    return (
      bynderDocument.originalUrl ||
      bynderDocument.selectedUrl ||
      bynderDocument.url ||
      bynderDocument.datUrl ||
      bynderDocument.previewUrl ||
      bynderDocument.previewImg ||
      ''
    );
  }, []);

  const getExtensionFromUrl = useCallback((url: string): string => {
    if (!url || url === '#') return '';

    try {
      const pathname = new URL(url, 'http://localhost').pathname;
      return pathname.split('.').pop()?.split(/[?#]/)[0] || '';
    } catch {
      return url.split('?')[0]?.split('#')[0]?.split('.').pop() || '';
    }
  }, []);

  const getBynderFilename = useCallback(
    (bynderDocument: SanityBynderDocument, fallbackLabel: string): string => {
      const url = getBynderDocumentUrl(bynderDocument);
      const filenameFromUrl = url ? decodeURIComponent(url.split('/').pop()?.split('?')[0] || '') : '';

      return (
        bynderDocument.originalFilename ||
        bynderDocument.label ||
        bynderDocument.name ||
        filenameFromUrl ||
        fallbackLabel
      );
    },
    [getBynderDocumentUrl],
  );

  // File URL resolution logic. Documents are served from Bynder.
  const getFileUrls = useCallback(
    async (sections: SanityDocumentSection[]): Promise<Record<string, FileUrlInfo>> => {
      const urls: Record<string, FileUrlInfo> = {};

      for (const section of sections) {
        // Only process visible sections
        if (section.isVisible === false) continue;

        for (const item of section.documentItems || []) {
          if (!item.bynderDocument) continue;

          const url = getBynderDocumentUrl(item.bynderDocument);
          const filename = getBynderFilename(item.bynderDocument, item.label || 'download');

          urls[item._key] = {
            url: url || '#',
            filename,
            extension: getExtensionFromUrl(url || filename),
            originalFilename: filename,
          };
        }
      }

      return urls;
    },
    [getBynderDocumentUrl, getBynderFilename, getExtensionFromUrl],
  );

  // Effect for file URL resolution
  useEffect(() => {
    if (documentSections?.length > 0) {
      getFileUrls(documentSections).then(setFileUrls);
    }
  }, [documentSections, getFileUrls]);

  // Memoized accordion items transformation
  const transformedAccordionItems = useMemo((): AccordionItem[] => {
    return (
      documentSections
        ?.filter((section: SanityDocumentSection) => section.isVisible !== false)
        ?.map((section: SanityDocumentSection) => ({
          id: section._key,
          title: section.heading ?? '',
          items:
            section?.documentItems?.map((link): AccordionLink => {
              if (link.linkType === 'external') {
                return {
                  id: link._key,
                  label: link.label,
                  url: link.externalUrl || '#',
                  type: 'external',
                };
              } else {
                // For file downloads, we need to pass additional metadata
                const fileInfo = fileUrls[link._key];
                return {
                  id: link._key,
                  label: link.label,
                  url: fileInfo?.url || '#',
                  type: 'file',
                  // Add file metadata for better download handling
                  metadata: {
                    filename: fileInfo?.filename || link.label,
                    extension: fileInfo?.extension || '',
                    originalFilename: fileInfo?.originalFilename || link.label,
                  },
                };
              }
            }) || [],
        })) || []
    );
  }, [documentSections, fileUrls]);

  // Memoized default expanded IDs
  const defaultExpandedIds = useMemo((): string[] => {
    if (defaultExpanded?.length > 0) {
      return (
        documentSections
          ?.filter(
            (item: SanityDocumentSection) =>
              item.isVisible !== false && defaultExpanded.includes(processHeading(item.heading).text),
          )
          ?.map((item) => item._key) || []
      );
    }

    // Set first panel as expanded by default if no defaultExpanded is specified
    const visibleSections = documentSections?.filter((item: SanityDocumentSection) => item.isVisible !== false);

    return visibleSections?.length > 0 ? [visibleSections[0]._key] : [];
  }, [documentSections, defaultExpanded]);

  return {
    // Component state
    fileUrls,

    // Processed data
    resolvedTitleText,
    cleanImagePosition,
    visibleSections,
    transformedAccordionItems,
    defaultExpandedIds,

    // Computed properties
    shouldRender,
    hasVisibleSections,
  };
}
